import type { ProjectTileViewModel } from '@rocco/ui/tile-project/view-models/project';
import type { ProductTileViewModel } from '@rocco/ui/tile-product/view-models/product';

/**
 * The prominent feature image (overview §3) + the "Talk to us" showcase images:
 * the professional's own featured projects, falling back to associated projects
 * (the most popular from their projects tab) when they have none — e.g. product
 * suppliers like Resene. Always projects, since a single large image reads best
 * as a project. Only items with a lead image are kept, in display order.
 */
export const getFeaturedWork = (
    ownProjects: ProjectTileViewModel[] | undefined,
    associatedProjects: ProjectTileViewModel[] | undefined,
): ProjectTileViewModel[] => {
    const own = (ownProjects ?? []).filter((p) => p.images?.[0]?.src);
    if (own.length > 0) return own;
    return (associatedProjects ?? []).filter((p) => p.images?.[0]?.src);
};

export type ShowcaseItem = ProjectTileViewModel | ProductTileViewModel;

interface SelectedShowcase {
    /** True when we fell back to products — callers relabel Projects → Products. */
    usingProducts: boolean;
    items: ShowcaseItem[];
}

/**
 * Items for the "(01 — Featured work)" grid, following the legacy overview's
 * category rule: manufacturers (product suppliers, e.g. Bath Co, Resene)
 * showcase their featured **products** — that grid reads best demoing what
 * they sell, even when they also carry projects — while everyone else shows
 * their featured projects. Where the legacy page hid the section when the
 * category's set was empty, this falls back to the other set so the grid
 * still renders. Only items with a lead image are kept, in display order.
 */
export const getSelectedShowcase = (
    ownProjects: ProjectTileViewModel[] | undefined,
    products: ProductTileViewModel[] | undefined,
    isManufacturer: boolean,
): SelectedShowcase => {
    const projects = (ownProjects ?? []).filter((p) => p.images?.[0]?.src);
    const productItems = (products ?? []).filter((p) => p.images?.[0]?.src);
    const usingProducts = isManufacturer
        ? productItems.length > 0
        : projects.length === 0 && productItems.length > 0;
    return {
        usingProducts,
        items: usingProducts ? productItems : projects,
    };
};
